import {
  useInfiniteQuery,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";

import {
  CustomNodeHistoryItem,
  getUserCustomNodesQueryOptions,
  searchCustomNodesQueryOptions,
} from "@/lib/api";

export type CustomNodeFilter = (string & {}) | "all" | "my_nodes" | "favorited";

export function useDefaultCustomNodes({
  suspense,
  filter = "all",
}: {
  suspense?: boolean;
  filter?: CustomNodeFilter;
} = {}) {
  let tagId: number | undefined;

  if (filter !== "all" && filter !== "my_nodes" && filter !== "favorited") {
    tagId = parseInt(filter);
    filter = "favorited";
  }

  const {
    data,
    isFetching: isFetchingUserCustomNodes,
    isRefetching,
  } = suspense
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useSuspenseQuery(getUserCustomNodesQueryOptions())
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery(getUserCustomNodesQueryOptions());
  const {
    data: favoritedNodes,
    isFetching: isFetchingFavoritedNodes,
    isRefetching: isRefetchingFavoritedNodes,
  } = suspense
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useSuspenseInfiniteQuery(
        // @ts-expect-error No clue
        searchCustomNodesQueryOptions({
          query: "",
          favorited_only: typeof tagId === "number" ? false : true,
          tags: typeof tagId === "number" ? [tagId] : undefined,
        }),
      )
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useInfiniteQuery(
        searchCustomNodesQueryOptions({
          query: "",
          favorited_only: typeof tagId === "number" ? false : true,
          tags: typeof tagId === "number" ? [tagId] : undefined,
        }),
      );

  const sortedData = useMemo(() => {
    const dataSet = new Map<number, CustomNodeHistoryItem>();
    const allData =
      filter === "my_nodes"
        ? data || []
        : filter === "favorited"
        ? favoritedNodes?.pages[0]?.results || []
        : [...(data || []), ...(favoritedNodes?.pages[0]?.results || [])];

    for (const node of allData) {
      if (!dataSet.has(node.id)) {
        dataSet.set(node.id, node);
      }
    }

    return Array.from(dataSet.values()).sort((a, b) => {
      if (a.favorited && !b.favorited) {
        return -1;
      }
      return 1;
    });
  }, [data, favoritedNodes, filter]);

  const isFetching =
    (isFetchingFavoritedNodes || isFetchingUserCustomNodes) &&
    !isRefetching &&
    !isRefetchingFavoritedNodes;

  return {
    data: isFetching ? undefined : sortedData,
    isFetching,
  };
}
